import { useEffect, useRef, useState, type FormEvent } from "react";
import { API_BASE } from "@/lib/api";
import { cn } from "@/lib/utils";
import { track } from "@/lib/analytics";
import { getPublicConfig } from "@/lib/public-config";
import "./support.css";

const TURNSTILE_SCRIPT_ID = "turnstile-script";
const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement,
        options: {
          sitekey: string;
          theme?: "light" | "dark" | "auto";
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
        },
      ) => string;
      reset: (widgetId: string) => void;
    };
  }
}

type SubmitState = "idle" | "submitting" | "success" | "error";

function ensureTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Turnstile can only run in the browser"));
  }
  if (window.turnstile) {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(
    TURNSTILE_SCRIPT_ID,
  ) as HTMLScriptElement | null;
  if (existingScript) {
    return new Promise((resolve, reject) => {
      existingScript.addEventListener("load", () => resolve(), { once: true });
      existingScript.addEventListener(
        "error",
        () => reject(new Error("Failed to load Turnstile script")),
        { once: true },
      );
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });
}

export default function SupportPage() {
  const [revealed, setRevealed] = useState(false);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState("");
  const [configLoaded, setConfigLoaded] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileReady, setTurnstileReady] = useState(false);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);

  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    requestAnimationFrame(() => setRevealed(true));
  }, []);

  useEffect(() => {
    const config = getPublicConfig();
    setTurnstileSiteKey(config.turnstileSiteKey);
    setConfigLoaded(true);
  }, []);

  useEffect(() => {
    if (!turnstileSiteKey || !turnstileContainerRef.current) {
      return;
    }

    let cancelled = false;

    ensureTurnstileScript()
      .then(() => {
        if (cancelled || !turnstileContainerRef.current || !window.turnstile) {
          return;
        }
        if (turnstileWidgetIdRef.current) {
          return;
        }

        turnstileWidgetIdRef.current = window.turnstile.render(
          turnstileContainerRef.current,
          {
            sitekey: turnstileSiteKey,
            theme: "dark",
            callback: (token: string) => {
              setTurnstileToken(token);
              setTurnstileReady(true);
            },
            "expired-callback": () => {
              setTurnstileToken("");
              setTurnstileReady(false);
            },
            "error-callback": () => {
              setTurnstileToken("");
              setTurnstileReady(false);
            },
          },
        );
      })
      .catch(() => {
        if (!cancelled) {
          setTurnstileReady(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [turnstileSiteKey]);

  const submitDisabled =
    submitState === "submitting" ||
    !name.trim() ||
    !email.trim() ||
    !message.trim() ||
    !turnstileToken ||
    !turnstileSiteKey ||
    !turnstileReady;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitDisabled) return;

    setSubmitState("submitting");
    setSubmitError(null);
    setSubmitMessage(null);

    track("tr_support_submit_attempt", {
      has_name: Boolean(name.trim()),
      has_email: Boolean(email.trim()),
      message_length: message.trim().length,
    });

    try {
      const response = await fetch(`${API_BASE}/support`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          turnstileToken,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { success?: boolean; message?: string; emailSent?: boolean }
        | null;

      if (!response.ok || !payload?.success) {
        const apiMessage = payload?.message || "Support request failed.";
        throw new Error(apiMessage);
      }

      setSubmitState("success");
      setName("");
      setEmail("");
      setMessage("");
      setTurnstileToken("");
      setTurnstileReady(false);
      setSubmitMessage(
        payload.emailSent === false
          ? "Request saved. Email forwarding will retry shortly."
          : "Support request sent. We will get back to you soon.",
      );
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }

      track("tr_support_submit_success", {
        email_sent: payload.emailSent !== false,
      });
    } catch (error) {
      setSubmitState("error");
      setSubmitError(
        error instanceof Error ? error.message : "Support request failed.",
      );
      setTurnstileToken("");
      setTurnstileReady(false);
      if (turnstileWidgetIdRef.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetIdRef.current);
      }

      track("tr_support_submit_error", {
        reason: error instanceof Error ? error.message : "unknown",
      });
    }
  }

  return (
    <div className="support-page">
      <section className={cn("support-shell", revealed && "revealed")}>
        <p className="support-eyebrow">CONTACT SUPPORT</p>
        <h1 className="support-title">Need Help With Tomorrow&apos;s Run?</h1>
        <p className="support-subtitle">
          Send us a support request and we&apos;ll route it directly to the team.
        </p>

        <form className="support-form" onSubmit={handleSubmit}>
          <label className="support-label" htmlFor="support-name">
            Name
          </label>
          <input
            id="support-name"
            type="text"
            className="support-input"
            autoComplete="name"
            maxLength={255}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
          />

          <label className="support-label" htmlFor="support-email">
            Email
          </label>
          <input
            id="support-email"
            type="email"
            className="support-input"
            autoComplete="email"
            maxLength={320}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
          />

          <label className="support-label" htmlFor="support-message">
            Message
          </label>
          <textarea
            id="support-message"
            className="support-textarea"
            rows={7}
            minLength={10}
            maxLength={5000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            required
          />

          {!turnstileSiteKey && configLoaded ? (
            <div className="support-warning">
              Captcha is not configured. Set `BUN_PUBLIC_TURNSTILE_SITE_KEY`.
            </div>
          ) : turnstileSiteKey ? (
            <div className="support-captcha-wrap">
              <div ref={turnstileContainerRef} className="support-captcha" />
            </div>
          ) : null}

          {submitError ? <div className="support-error">{submitError}</div> : null}
          {submitMessage ? (
            <div className="support-success">{submitMessage}</div>
          ) : null}

          <button type="submit" className="support-submit" disabled={submitDisabled}>
            {submitState === "submitting" ? "Sending..." : "Send Support Request"}
          </button>
        </form>
      </section>
    </div>
  );
}
